import {
  CapiChatDialog,
  CapiChatDialogAdapter,
  CapiChatDialogConfig,
  createCapiChatDialogAdapter,
} from '@vcredit/fe-lib';
import { t } from 'i18next';
import { merge } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { CapiSelfIntroduction, capiSelfIntroduction } from '../../../api/capi';
import { FlagType, LayoutConfigResponse, queryCurrentVersion } from '../../../api/common';
import { nativeApi } from '../../../utils/nativeApi';
import { isEn } from '../../../utils/util';
import CapiAppIcon from '../CapiAppIcon';
import { CapiConfig, whatsAppConfig } from '../interface';
import WhatsApp from '../WhatsAppIcon';
import style from './index.module.less';

type AssistantState = 'pending' | 'capi' | 'whatsapp' | 'none';

interface FloatingAssistantProps {
  whatsApp?: whatsAppConfig | boolean;
  moduleHideWhatsApp?: boolean;
  capiConfig?: CapiConfig;
  onlyDialog?: boolean;
  [key: string]: unknown;
}

/**
 * FloatingAssistant: Unified assistant entry component that decides visibility between Capi and WhatsApp.
 * Rules:
 * - If layoutCategory exists: call API every time (no cache). Error/timeout => treat as Capi not shown.
 * - Only show WhatsApp when we are sure Capi will not be shown.
 * - Page/module level WhatsApp permission has highest priority.
 */
function FloatingAssistant(props: FloatingAssistantProps) {
  const { whatsApp, moduleHideWhatsApp = false, capiConfig, onlyDialog, ...otherProps } = props;

  const {
    layoutCategory,
    getCapiOtherParams,
    getCapiInputParams: getCapiInputParamsFromComp,
    capiImgProps,
    updateClCreditPage,
    confirmLoan,
    getIntroductionHint,
    capiCaseType,
  } = capiConfig || {};

  const [assistantState, setAssistantState] = useState<AssistantState>('none');

  const [apiShowMsg, setApiShowMsg] = useState<string>('');
  const [capiShowMsgFlag, setCapiShowMsgFlag] = useState<FlagType>('N');

  const [showCapiDialog, setShowCapiDialog] = useState(false);
  const [selfIntroduction, setSelfIntroduction] = useState<CapiSelfIntroduction | null>(null);

  // Highest priority: whether WhatsApp is permitted by page/module level configuration
  const isWhatsAppPermitted = useMemo(() => {
    if (typeof whatsApp !== 'undefined') {
      return whatsApp !== false;
    }
    return !moduleHideWhatsApp;
  }, [whatsApp, moduleHideWhatsApp]);

  // Decision flow: evaluate visibility state each time based on layoutCategory and API results
  useEffect(() => {
    let canceled = false;

    const decide = async () => {
      // If no layoutCategory, we directly decide WhatsApp based on permission
      if (!layoutCategory) {
        if (isWhatsAppPermitted) {
          setAssistantState('whatsapp');
        } else {
          setAssistantState('none');
        }
        return;
      }

      // With layoutCategory, enter pending and call API (no cache)
      setAssistantState('pending');
      try {
        const response = await queryCurrentVersion(layoutCategory);
        if (canceled) return;

        const config: LayoutConfigResponse = response;
        setCapiShowMsgFlag(config.capiShowMsgFlag || 'N');
        if (config.capiShowFlag === 'Y' && config.capiShowMsgFlag === 'Y') {
          setApiShowMsg(config.capiShowMsg);
        } else {
          setApiShowMsg('');
        }

        // Decide final state: show Capi only when API returns Y; otherwise WhatsApp if permitted
        if (config.capiShowFlag === 'Y') {
          setAssistantState('capi');
        } else {
          setAssistantState(isWhatsAppPermitted ? 'whatsapp' : 'none');
        }
      } catch (error) {
        // Error/timeout: treat as Capi not shown
        setApiShowMsg('');
        setCapiShowMsgFlag('N');
        setAssistantState(isWhatsAppPermitted ? 'whatsapp' : 'none');
      }
    };

    decide();
    return () => {
      canceled = true;
    };
    // Re-decide whenever inputs change
  }, [layoutCategory, isWhatsAppPermitted]);

  // Fetch Capi self introduction only when we will show Capi
  useEffect(() => {
    if (assistantState !== 'capi' || !layoutCategory) {
      return;
    }
    let canceled = false;
    const fetchSelfIntroduction = async () => {
      try {
        const res = await capiSelfIntroduction({
          appCode: 'CreFIT-Capi',
          source: 'OTHER',
          layoutCategory,
        });
        if (!canceled) {
          setSelfIntroduction(res);
          console.log('Capi 自我介绍:', res);
        }
      } catch (error) {
        console.error('请求 Capi 自我介绍失败:', error);
      }
    };

    fetchSelfIntroduction();
    return () => {
      canceled = true;
    };
  }, [assistantState, layoutCategory]);

  const openingRemarksMsg = useMemo(() => {
    if (!selfIntroduction) {
      return '';
    }
    if (!getIntroductionHint) {
      return selfIntroduction?.app.openingRemarksMsg || '';
    }
    return getIntroductionHint(selfIntroduction?.app.openingRemarksMsg || '');
  }, [selfIntroduction, getIntroductionHint]);

  const dialogConfig: CapiChatDialogConfig = {
    layoutCategory,
    enableVoice: true,
    showVoiceButton: true,
    robotName: 'Capi',
    introductionTitle: t<string>('capi.introductionTitle'),
    introductionDesc: openingRemarksMsg ?? '',
    appCode: 'CreFIT-Capi',
    assistantId: '3',
    capiCaseType,
    getCapiOtherParams,
    getCapiInputParams: async () =>
      merge(
        {
          ...(await getCapiInputParamsFromComp?.()),
        },
        {
          inputs: {
            fastLoan: capiShowMsgFlag,
            layoutCategory,
          },
        }
      ),
    updateClCreditPage,
    confirmLoan,
    language: isEn() ? 'en' : 'zh',
  };

  const adapter: CapiChatDialogAdapter = useMemo(
    () =>
      createCapiChatDialogAdapter({
        getToken: () => nativeApi.getToken(),
        getLanguage: () => nativeApi.getLanguage(),
        getDeviceId: () => nativeApi.getDeviceId?.() || '',
      }),
    []
  );

  const handleCapiClick = () => {
    // keep original tracking behavior if present
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any)?.sensors?.track?.('AI_Capi_Icon', {
        Case_Type: capiCaseType,
      });
    } catch (e) {
      // silent
    }
    setShowCapiDialog(true);
  };

  if (onlyDialog) {
    return assistantState === 'capi' ? (
      <CapiChatDialog adapter={adapter} config={dialogConfig} {...otherProps} />
    ) : (
      <div />
    );
  }

  // Render by state machine
  if (assistantState === 'pending') {
    return <div />;
  }
  if (assistantState === 'capi') {
    return (
      <div className={style.assistantContainer}>
        <CapiAppIcon onClick={handleCapiClick} prop={capiImgProps} ApiShowMsg={apiShowMsg} />
        <CapiChatDialog
          adapter={adapter}
          config={dialogConfig}
          visible={showCapiDialog}
          setVisible={setShowCapiDialog}
        />
      </div>
    );
  }
  if (assistantState === 'whatsapp') {
    return (
      <div className={style.assistantContainer}>
        <WhatsApp prop={whatsApp} />
      </div>
    );
  }
  // none
  return <div className={style.assistantContainer} />;
}

export default FloatingAssistant;
